import { createApiClient } from '@iptv/api-client';

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export const api = createApiClient({
  baseUrl,
  defaultHeaders: { Accept: 'application/json' },
});

const TOKEN_KEY = 'iptv_admin_access_token';
const REFRESH_KEY = 'iptv_admin_refresh_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export interface ApiError {
  status: number;
  detail: string;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Pass FormData here when uploading files. The browser sets its own
   *  multipart Content-Type with boundary; we strip our default. */
  formData?: FormData;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
}

export async function adminFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, baseUrl || window.location.origin);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }

  const token = getAccessToken();
  const isMultipart = options.formData !== undefined;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers ?? {}),
  };
  if (!isMultipart) {
    if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
  }
  const body: BodyInit | undefined = isMultipart
    ? options.formData
    : options.body !== undefined
      ? JSON.stringify(options.body)
      : undefined;
  const resp = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers,
    body,
    credentials: 'include',
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!resp.ok) {
    const detail =
      (parsed && typeof parsed === 'object' && 'detail' in parsed
        ? String((parsed as { detail: unknown }).detail)
        : null) ?? `HTTP ${resp.status}`;
    const err: ApiError = { status: resp.status, detail };
    throw err;
  }

  return parsed as T;
}
